import { Head } from '@inertiajs/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import SyntaxHighlighter from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import { Trash2, Key, FileText, AlertTriangle, CheckCircle, Terminal } from 'lucide-react'

export default function DeletingFiles() {
  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      <Head title="Deleting Files - Documentation" />
      
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Deleting Files</h1>
        <p className="text-lg text-gray-600">
          Learn how to delete files using the admin dashboard or programmatically via the API.
        </p>
      </div>

      {/* Dashboard Method */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Delete Files via Dashboard
          </CardTitle>
          <CardDescription>
            The simplest way to manage and delete your files
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600">
            You can view and delete your files at any time using this admin dashboard by clicking on <strong>Dashboard</strong> in the left hand nav bar.
          </p>
        </CardContent>
      </Card>

      {/* API Method */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Terminal className="w-5 h-5" />
            Delete Files via API
          </CardTitle>
          <CardDescription>
            Programmatically delete files using our REST API
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step 1: Generate API Keys */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Badge className="bg-blue-500">Step 1</Badge>
              Generate API Keys
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-600 ml-4">
              <li>In the left hand nav bar, click <strong>Manage API keys</strong>.</li>
              <li>Click on <strong>Generate New Private API Key</strong> to generate a public/private API key pair</li>
              <li>This will generate a public token and a secret token</li>
            </ol>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-amber-500 mr-2 mt-0.5" />
                <div className="text-sm text-amber-700">
                  <strong>Important:</strong> These keys are <strong>different</strong> from the key used to upload files in the widget. The secret token gives full access to your account, so keep it secret!
                </div>
              </div>
            </div>
          </div>

          {/* Step 2: Test the API */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Badge className="bg-blue-500">Step 2</Badge>
              Test Your API Connection
            </h3>
            <p className="text-gray-600 mb-3">
              Click on "View API Documentation" to jump to the API documentation. Try out the API with the sample code provided:
            </p>
            <SyntaxHighlighter
              language="bash"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            >
{`curl -X GET \\
  -u "yourPublicKey:yourSecretKey" \\
  https://app.simplefileupload.com/api/v1/teams`}
            </SyntaxHighlighter>
            <p className="text-sm text-gray-600 mt-3">
              If things go well you will receive a terminal response that shows you your team name and creation date.
            </p>
          </div>

          {/* Step 3: Delete Files */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Badge className="bg-red-500">Step 3</Badge>
              Delete Files
            </h3>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
                <div className="text-sm text-red-700">
                  <strong>WARNING:</strong> A delete request will immediately delete the file. This action cannot be undone.
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Delete by URL */}
              <div>
                <h4 className="font-medium mb-2">Delete by URL</h4>
                <p className="text-sm text-gray-600 mb-2">
                  To delete a file by URL, send the file URL you have saved as the url parameter to the delete endpoint:
                </p>
                <SyntaxHighlighter
                  language="bash"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`curl -X DELETE \\
  -u "p9ea67c85ebc7a43d765e6125b83e6cc4:sbde6eb4206d781dcdfbc" \\
  "https://app.simplefileupload.com/api/v1/file?url=the_complete_file_url_you_have_saved"`}
                </SyntaxHighlighter>
              </div>

              {/* Delete by ID */}
              <div>
                <h4 className="font-medium mb-2">Delete by ID</h4>
                <p className="text-sm text-gray-600 mb-2">
                  To delete a file by ID, use the api/v1/files/:id format:
                </p>
                <SyntaxHighlighter
                  language="bash"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`curl -X DELETE \\
  -u "p9ea67c85ebc7a43d765e6125b83e6cc4:sbde6eb4206d781dcdfbcd" \\
  https://app.simplefileupload.com/api/v1/files/:id`}
                </SyntaxHighlighter>
              </div>

              {/* Success Response */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm text-green-700">
                      If the delete request is successful, you will receive the following message:
                    </p>
                    <code className="text-sm text-green-800 font-mono">File successfully destroyed</code>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Reference */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Reference</CardTitle>
          <CardDescription>
            API endpoints for file deletion
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Badge variant="destructive">DELETE</Badge>
              <code className="text-sm bg-gray-100 px-2 py-1 rounded">/api/v1/file?url=FILE_URL</code>
              <span className="text-sm text-gray-600">Delete by URL</span>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="destructive">DELETE</Badge>
              <code className="text-sm bg-gray-100 px-2 py-1 rounded">/api/v1/files/:id</code>
              <span className="text-sm text-gray-600">Delete by ID</span>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="secondary">GET</Badge>
              <code className="text-sm bg-gray-100 px-2 py-1 rounded">/api/v1/files</code>
              <span className="text-sm text-gray-600">List all files</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
